/**
 * 主体管理页面
 * 管理投标主体公司信息
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuildingOfficeIcon, PlusIcon, PencilIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { CompanyInfo } from '../types';
import { companyApi } from '../services/api';
import Toast, { ToastType } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';

const CompanyManagement: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ show: boolean; companyId?: string; companyName?: string }>({ show: false });
  
  const [toast, setToast] = useState<{
    show: boolean;
    type: ToastType;
    title: string;
    messages?: string[];
  }>({
    show: false,
    type: 'info',
    title: '',
    messages: []
  });

  // 加载公司列表
  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await companyApi.getCompanies();
      if (response.data) {
        setCompanies(response.data.map((c: any) => ({
          id: c.id.toString(),
          name: c.name,
          fullName: c.full_name || c.name
        })));
      }
    } catch (error) {
      console.error('加载公司列表失败:', error);
      showToast('error', '加载失败', ['请检查网络连接后重试']);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (type: ToastType, title: string, messages?: string[]) => {
    setToast({ show: true, type, title, messages });
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  // 跳转到添加页面
  const handleAdd = () => {
    navigate('/companies/new');
  };

  // 跳转到编辑页面
  const handleEdit = (companyId: string) => {
    navigate(`/companies/${companyId}/edit`);
  };

  // 显示删除确认
  const handleDeleteClick = (company: CompanyInfo) => {
    setDeleteDialog({ show: true, companyId: company.id, companyName: company.name });
  };

  // 确认删除
  const handleConfirmDelete = async () => {
    if (!deleteDialog.companyId) return;
    
    try {
      setLoading(true);
      await companyApi.deleteCompany(deleteDialog.companyId);
      showToast('success', '删除成功');
      loadCompanies(); // 重新加载列表
    } catch (error: any) {
      console.error('删除失败:', error);
      showToast('error', '删除失败', [error.response?.data?.detail || '请检查网络连接后重试']);
    } finally {
      setLoading(false);
      setDeleteDialog({ show: false });
    }
  };

  return (
    <div className="h-full flex flex-col bg-transparent">
      {/* Toast 通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        messages={toast.messages}
        onClose={closeToast}
      />

      {/* 确认删除对话框 */}
      <ConfirmDialog
        isOpen={deleteDialog.show}
        title="确认删除主体？"
        message={`您确定要删除以下主体吗？\n\n${deleteDialog.companyName || ''}\n\n⚠️ 删除后将无法恢复！`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialog({ show: false })}
      />

      {/* 头部 */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">主体管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理投标主体的企业信息</p>
          </div>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-medium"
          >
            <PlusIcon className="w-5 h-5" />
            新增主体
          </button>
        </div>
      </div>

      {/* 渐变分割线 */}
      <div className="mx-6 mt-2 h-px bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400 rounded-full" />

      {/* 主体列表 */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <ArrowPathIcon className="w-8 h-8 animate-spin mb-3" />
            <p className="text-base">加载中...</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <BuildingOfficeIcon className="w-16 h-16 mb-4" />
            <p className="text-lg">暂无主体信息</p>
            <p className="text-sm mt-2">点击右上角"新增主体"添加企业</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map((company) => (
              <div
                key={company.id}
                className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <BuildingOfficeIcon className="w-7 h-7 text-blue-600" />
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleEdit(company.id)}
                      className="text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(company)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2">{company.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{company.fullName}</p>

                <div className="border-t border-gray-100 pt-3 mt-3">
                  <button 
                    onClick={() => handleEdit(company.id)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    完善企业信息 →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyManagement;
